/**
 * Currency Service
 * Manages currency formatting based on church settings
 */

import { supabase } from './supabase'
import { CURRENCIES, type SupportedCurrency, formatCurrency as formatCurrencyUtil, formatWithUSDEquivalent } from './currencyUtils'

// Cache for church settings
let cachedChurchId: string | null = null
let cachedCurrency: SupportedCurrency = 'GHS'
let cacheTimestamp: number = 0
const CACHE_DURATION = 5 * 60 * 1000 // 5 minutes

/**
 * Get church currency from settings
 */
export async function getChurchCurrency(churchId?: string): Promise<SupportedCurrency> {
  // Use cache if available and fresh
  if (
    cachedChurchId === churchId &&
    Date.now() - cacheTimestamp < CACHE_DURATION &&
    cachedCurrency
  ) {
    return cachedCurrency
  }

  try {
    let targetChurchId = churchId

    // If no churchId provided, get from current user
    if (!targetChurchId) {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) return 'GHS' // Default fallback

      const { data: profile } = await supabase
        .from('profiles')
        .select('church_id')
        .eq('id', user.id)
        .single()

      targetChurchId = profile?.church_id
    }

    if (!targetChurchId) return 'GHS'

    // Fetch church settings
    const { data: church, error } = await supabase
      .from('churches')
      .select('settings')
      .eq('id', targetChurchId)
      .single()

    if (error || !church) {
      console.warn('Failed to fetch church currency, using default:', error)
      return 'GHS'
    }

    const currency = (church.settings as any)?.currency as SupportedCurrency || 'GHS'

    // Update cache
    cachedChurchId = targetChurchId
    cachedCurrency = currency
    cacheTimestamp = Date.now()

    return currency
  } catch (error) {
    console.error('Error getting church currency:', error)
    return 'GHS'
  }
}

/**
 * Get all church settings
 */
export async function getChurchSettings(churchId?: string) {
  try {
    let targetChurchId = churchId

    if (!targetChurchId) {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) return null

      const { data: profile } = await supabase
        .from('profiles')
        .select('church_id')
        .eq('id', user.id)
        .single()

      targetChurchId = profile?.church_id
    }

    if (!targetChurchId) return null

    const { data: church } = await supabase
      .from('churches')
      .select('settings')
      .eq('id', targetChurchId)
      .single()

    return church?.settings || {}
  } catch (error) {
    console.error('Error getting church settings:', error)
    return null
  }
}

/**
 * Format amount with church's default currency
 */
export async function formatAmount(amount: number, churchId?: string): Promise<string> {
  const currency = await getChurchCurrency(churchId)
  return formatCurrencyUtil(amount, currency)
}

/**
 * Format amount with USD equivalent if enabled in settings
 */
export async function formatAmountWithEquivalent(amount: number, churchId?: string): Promise<string> {
  const settings = await getChurchSettings(churchId) as any
  const currency = (settings?.currency as SupportedCurrency) || 'GHS'
  const showUSD = settings?.show_usd_equivalent || false

  if (showUSD && currency !== 'USD') {
    return formatWithUSDEquivalent(amount, currency)
  }

  return formatCurrencyUtil(amount, currency)
}

/**
 * Clear currency cache (call when settings change)
 */
export function clearCurrencyCache() {
  cachedChurchId = null
  cachedCurrency = 'GHS'
  cacheTimestamp = 0
}

/**
 * Get currency symbol for church
 */
export async function getChurchCurrencySymbol(churchId?: string): Promise<string> {
  const currency = await getChurchCurrency(churchId)
  return CURRENCIES[currency]?.symbol || currency
}

/**
 * Check if multi-currency is enabled
 */
export async function isMultiCurrencyEnabled(churchId?: string): Promise<boolean> {
  const settings = await getChurchSettings(churchId) as any
  return settings?.enable_multi_currency || false
}

/**
 * Format currency code with amount (e.g., "GHS 1,500.00")
 */
export function formatCurrencyWithCode(amount: number, currency: SupportedCurrency): string {
  return `${currency} ${amount.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })}`
}

// Export the currencies for dropdown/selection UI
export { CURRENCIES, type SupportedCurrency } from './currencyUtils'
